/**
 * AppointmentCard — single, sleek card for the unified Appointments
 * landing. Mirrors the cart-card visual standard (Bill 2026-04-23):
 *   - Customer name + appointment subject up top
 *   - Time row with relative chip ("in 30 min")
 *   - Channel chip (in-branch / phone / Teams / Bookings)
 *   - Footer: assigned officer · branch · type
 *   - CTAs: Join (Teams) / Mark arrived (in-branch) / Reschedule / Cancel
 *
 * NEVER renders: appointmentId, calendarEventId, onlineMeetingId, raw join URL,
 * bookingsId. The Join button consumes joinWebUrl internally.
 */

import { useMemo } from 'react';
import { Pill, Button } from '@/components/primitives';
import type { Appointment, AppointmentChannel } from '../types';
import { JoinMeetingButton } from './JoinMeetingButton';

const CHANNEL_LABEL: Record<AppointmentChannel, { label: string; icon: string }> = {
  inBranch: { label: 'In-branch', icon: '🏢' },
  phone: { label: 'Phone', icon: '📞' },
  teams: { label: 'Teams meeting', icon: '💻' },
  bookings: { label: 'Self-booked', icon: '📅' },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = d.toDateString() === today.toDateString();
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  if (sameDay) return 'Today';
  if (isTomorrow) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function relativeLabel(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < -60_000) return 'started';
  const mins = Math.round(ms / 60_000);
  if (mins <= 0) return 'now';
  if (mins < 60) return `in ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs} hr`;
  return null;
}

export interface AppointmentCardProps {
  appointment: Appointment;
  /** Officer-side card actions. Hide on customer-facing surfaces. */
  onMarkArrived?: (a: Appointment) => void;
  onReschedule?: (a: Appointment) => void;
  onCancel?: (a: Appointment) => void;
  /** Hide the secondary action buttons in dense lists. */
  compact?: boolean;
}

export function AppointmentCard({
  appointment,
  onMarkArrived,
  onReschedule,
  onCancel,
  compact = false,
}: AppointmentCardProps): JSX.Element {
  const startLabel = useMemo(() => formatTime(appointment.startUtc), [appointment.startUtc]);
  const dayLabel = useMemo(() => formatDayLabel(appointment.startUtc), [appointment.startUtc]);
  const rel = useMemo(() => relativeLabel(appointment.startUtc), [appointment.startUtc]);
  const channel = CHANNEL_LABEL[appointment.channel];

  const isToday = dayLabel === 'Today';

  return (
    <article
      className={`appt-card ${appointment.cancelled ? 'is-cancelled' : ''}`.trim()}
      aria-label={`${appointment.subject} for ${appointment.customerName}`}
    >
      <header className="appt-card__head">
        <div className="appt-card__title-row">
          <span className="appt-card__subject">{appointment.subject}</span>
          <span className="appt-card__sep" aria-hidden>·</span>
          <span className="appt-card__customer">{appointment.customerName}</span>
        </div>
        <div className="appt-card__chips">
          <span className={`appt-chip appt-chip--${appointment.channel}`}>
            <span aria-hidden>{channel.icon}</span> {channel.label}
          </span>
          {appointment.source === 'bookings' && (
            <Pill variant="info">Bookings</Pill>
          )}
          {appointment.cancelled && <Pill variant="err">Cancelled</Pill>}
          {appointment.arrived && <Pill variant="ok">Arrived</Pill>}
        </div>
      </header>

      <div className="appt-card__time">
        <span className="appt-card__time-day">{dayLabel}</span>
        <span className="appt-card__time-sep" aria-hidden>·</span>
        <span className="appt-card__time-clock">{startLabel}</span>
        {rel && isToday && (
          <span className={`appt-card__time-rel ${rel === 'now' || rel === 'started' ? 'is-now' : ''}`.trim()}>
            {rel}
          </span>
        )}
      </div>

      {appointment.notes && (
        <p className="appt-card__notes">{appointment.notes}</p>
      )}

      <footer className="appt-card__foot">
        <div className="appt-card__meta">
          {appointment.assignedOfficer && (
            <span className="appt-card__officer">
              <span className="appt-card__officer-avatar" aria-hidden>
                {appointment.assignedOfficerInitials ?? appointment.assignedOfficer.slice(0, 1)}
              </span>
              {appointment.assignedOfficer}
            </span>
          )}
          {appointment.branchName && (
            <span className="appt-card__branch">{appointment.branchName}</span>
          )}
        </div>

        {!compact && (
          <div className="appt-card__cta">
            {appointment.channel === 'teams' && appointment.joinWebUrl && (
              <JoinMeetingButton joinWebUrl={appointment.joinWebUrl} />
            )}
            {appointment.channel === 'inBranch' && !appointment.arrived && onMarkArrived && (
              <Button size="sm" variant="green" onClick={() => onMarkArrived(appointment)}>
                Mark arrived
              </Button>
            )}
            {onReschedule && !appointment.cancelled && (
              <Button size="sm" variant="ghost" onClick={() => onReschedule(appointment)}>
                Reschedule
              </Button>
            )}
            {onCancel && !appointment.cancelled && (
              <Button size="sm" variant="ghost" onClick={() => onCancel(appointment)}>
                Cancel
              </Button>
            )}
          </div>
        )}
      </footer>
    </article>
  );
}
